<template>
  <SfDropdown
    :dropdown-id="dropdownId"
    :is-open="isOpen"
    variant="seamless"
    unstyled-trigger
    @dropdown-clicked="toggle"
    @dropdown-closed="close"
  >
    <template #trigger>
      <SfButtonDs variant="secondary" size="lg" icon-after="caret-down">
        {{ triggerLabel }}
      </SfButtonDs>
    </template>
    <template #menu>
      <div class="tw-p-2">
        <SfList no-margin>
          <SfListItem
            v-for="option in options"
            :key="option.value"
            :item-id="option.value"
            variant="md"
            cornered
            :selected="option.value === modelValue"
            @item-click="select(option.value)"
          >
            {{ I18n.t(option.labelKey) }}
          </SfListItem>
        </SfList>
      </div>
    </template>
  </SfDropdown>
</template>

<script setup lang="ts">
import type { FilterOption } from '@app/application/user-management/types'

import { computed } from 'vue'
import { storeToRefs } from 'pinia'

import { SfDropdown } from '@app/shared/vue/components/sf-dropdown'
import SfButtonDs from '@app/shared/vue/components/sf-button/sf-button-ds.vue'
import SfList from '@app/shared/vue/components/sf-list/sf-list.vue'
import SfListItem from '@app/shared/vue/components/sf-list/sf-list-item.vue'
import useI18n from '@app/shared/vue/composables/use-I18n'
import { useDropdown } from '@app/shared/pinia/dropdown'

defineOptions({ name: 'UsersFilterDropdown' })

interface Props {
  label: string
  options: FilterOption[]
  modelValue: string
  dropdownId: string
}

const props = defineProps<Props>()

const emit =
  defineEmits<{
    'update:modelValue': [value: string]
  }>()

const I18n = useI18n()
const dropdownStore = useDropdown()
const { currentDropdownId } = storeToRefs(dropdownStore)

const isOpen = computed(() => currentDropdownId.value === props.dropdownId)

const selectedOption = computed(() =>
  props.options.find((option) => option.value === props.modelValue)
)

const triggerLabel = computed(() =>
  I18n.t('user.filters.trigger', {
    label: props.label,
    value: selectedOption.value ? I18n.t(selectedOption.value.labelKey) : '',
  })
)

function toggle(): void {
  dropdownStore.toggleDropdown(props.dropdownId)
}

function close(): void {
  dropdownStore.closeDropdown()
}

function select(value: string): void {
  emit('update:modelValue', value)
  close()
}
</script>
